import { useEffect, useMemo, useRef, useState } from 'react';
import type { MagicState, MagicTraceStep } from '@captionflow/shared';
import { api, type AdminUser, type AdminUsage, type AdminUserProjectsResponse } from '../api/client';
import { CaptionPreviewGallery } from '../components/CaptionPreviewGallery';

/**
 * /admin shell: role-gated by the AdminGuard route wrapper. Two tabs - "kMagic"
 * (the run inspector) and "Users" (every user on the platform, expand a row to
 * see that user's projects).
 */
export function AdminPage() {
  const [tab, setTab] = useState<'kmagic' | 'users'>('kmagic');
  return (
    <div style={S.page}>
      <div style={S.nav}>
        <span style={S.brand}>admin</span>
        <button
          style={{ ...S.tab, ...(tab === 'kmagic' ? S.tabActive : {}) }}
          onClick={() => setTab('kmagic')}
        >
          kMagic
        </button>
        <button
          style={{ ...S.tab, ...(tab === 'users' ? S.tabActive : {}) }}
          onClick={() => setTab('users')}
        >
          Users
        </button>
        <a href="/" style={S.link}>← Home</a>
      </div>
      {tab === 'kmagic' ? <KmagicAdminPanel /> : <UsersPanel />}
    </div>
  );
}

/** "Users" tab: all users, newest first; expand a row to lazy-load projects. */
function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    api
      .adminListUsers()
      .then((r) => setUsers(r.users))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load users'));
  }, []);

  if (error) return <div style={S.empty}>{error}</div>;
  if (!users) return <div style={S.empty}>Loading users…</div>;
  if (users.length === 0) return <div style={S.empty}>No users yet.</div>;

  return (
    <section style={{ ...S.card }}>
      <h3 style={S.h3}>All users ({users.length})</h3>
      <p style={{ color: '#777', fontSize: 12, margin: '0 0 14px' }}>
        Usage per user (expand for per-project + monthly): <b style={{ color: '#a06bff' }}>kMagic</b> runs +{' '}
        <b style={{ color: '#ffd54a' }}>$</b> (real OpenRouter spend) ·{' '}
        <b style={{ color: '#42c6ff' }}>render min</b> (render wall-time, the compute signal) ·{' '}
        <b style={{ color: '#39d98a' }}>mattes</b> (Modal depth).
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {users.map((u) => (
          <UserRow
            key={u.id}
            user={u}
            open={expanded === u.id}
            onToggle={() => setExpanded((cur) => (cur === u.id ? null : u.id))}
          />
        ))}
      </div>
    </section>
  );
}

/** kMagic runs · render wall-minutes · Modal mattes, as compact chips. Dims a
 *  metric that's zero so non-zero usage stands out. */
function UsageChips({ u, size = 12 }: { u: AdminUsage; size?: number }) {
  const chip = (label: string, value: number, color: string) => (
    <span style={{ ...S.usageChip, fontSize: size, color: value ? color : '#555', background: value ? color + '18' : 'transparent' }}>
      {value % 1 === 0 ? value : value.toFixed(1)} {label}
    </span>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {chip('kMagic', u.kmagicRuns, '#a06bff')}
      <span style={{ ...S.usageChip, fontSize: size, color: u.kmagicCostUsd ? '#ffd54a' : '#555', background: u.kmagicCostUsd ? '#ffd54a18' : 'transparent' }}>
        ${u.kmagicCostUsd < 0.01 && u.kmagicCostUsd > 0 ? u.kmagicCostUsd.toFixed(4) : u.kmagicCostUsd.toFixed(2)}
      </span>
      {chip('render min', u.renderMinutes, '#42c6ff')}
      {chip('mattes', u.depthMattes, '#39d98a')}
    </span>
  );
}

/** One user row + its (lazily fetched) projects, usage total, and monthly
 *  breakdown when expanded. */
function UserRow({ user, open, onToggle }: { user: AdminUser; open: boolean; onToggle: () => void }) {
  const [data, setData] = useState<AdminUserProjectsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data !== null) return;
    setLoading(true);
    api
      .adminUserProjects(user.id)
      .then(setData)
      .catch(() => setData({ projects: [], total: { kmagicRuns: 0, kmagicCostUsd: 0, renderMinutes: 0, depthMattes: 0 }, byMonth: [] }))
      .finally(() => setLoading(false));
  }, [open, data, user.id]);

  return (
    <div style={S.userRow}>
      <button style={S.userHead} onClick={onToggle}>
        <span style={{ color: '#888', width: 14 }}>{open ? '▾' : '▸'}</span>
        <span style={{ color: '#e7e7ea', fontWeight: 600 }}>{user.email}</span>
        {user.displayName && <span style={{ color: '#8b8f9c', fontSize: 12 }}>{user.displayName}</span>}
        {user.role === 'admin' && <span style={{ ...S.pill, color: '#a06bff', background: '#a06bff22' }}>admin</span>}
        {user.plan && <span style={{ ...S.pill, color: '#39d98a', background: '#39d98a22' }}>{user.plan}</span>}
        {!user.emailVerified && <span style={{ ...S.pill, color: '#f5a623', background: '#f5a62322' }}>unverified</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <UsageChips u={user.usage} />
          <span style={{ color: '#8b8f9c', fontSize: 12 }}>
            {user.projectCount} project{user.projectCount === 1 ? '' : 's'}
          </span>
        </span>
      </button>
      {open && (
        <div style={S.userBody}>
          {loading ? (
            <span style={{ color: '#666', fontSize: 13 }}>Loading…</span>
          ) : (
            <>
              {data && (data.total.kmagicRuns || data.total.renderMinutes || data.total.depthMattes) ? (
                <div style={S.usageSummary}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ color: '#8b8f9c', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total usage</span>
                    <UsageChips u={data.total} size={12.5} />
                  </div>
                  {data.byMonth.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {data.byMonth.map((m) => (
                        <div key={m.month} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: '#8aa0ff', fontSize: 12, fontFamily: 'monospace', minWidth: 62 }}>{m.month}</span>
                          <UsageChips u={m} size={11.5} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}

              {!data?.projects.length ? (
                <span style={{ color: '#666', fontSize: 13 }}>No projects.</span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {data.projects.map((p) => (
                    <div key={p.id} style={S.projRow}>
                      <span style={{ color: '#cfd', fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                      <span style={{ ...S.pill, color: '#8aa0ff', background: '#8aa0ff22' }}>{p.status}</span>
                      <span style={{ color: '#888', fontSize: 12 }}>{p.aspectRatio}</span>
                      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <UsageChips u={p.usage} size={11.5} />
                        <a href={`/admin?project=${p.id}`} style={{ ...S.link, fontSize: 12 }}>kMagic ↗</a>
                        <span style={{ color: '#555', fontSize: 11, fontFamily: 'monospace' }}>{p.id.slice(-8)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The kMagic tab of /admin: what kMagic is doing behind the scenes - sampled
 * frames, the agentic AI log, and the resulting plan. Rendered inside the
 * admin shell (see AdminPage), which owns the nav + role gate.
 */
function KmagicAdminPanel() {
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const initial = new URLSearchParams(location.search).get('project') ?? '';
  const [projectId, setProjectId] = useState(initial);
  const [runId, setRunId] = useState('');
  const [magic, setMagic] = useState<
    (MagicState & { frameUrls?: string[]; runs?: { id?: string; status: string; startedAt?: string; instruction?: string }[] }) | null
  >(null);
  const [cutout, setCutout] = useState<Awaited<ReturnType<typeof api.getCutout>> | null>(null);
  // Once the cut-out is ready/failed we stop re-fetching it: each fetch presigns
  // a NEW url, which would reload the <video> and interrupt playback. kMagic keeps
  // polling; the frozen cut-out url just rides along without reloading.
  const cutoutSettled = useRef(false);
  const [renders, setRenders] = useState<Awaited<ReturnType<typeof api.getRecentRenders>>['jobs']>([]);

  useEffect(() => {
    api.listProjects().then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name })))).catch(() => undefined);
  }, []);

  // Reset the selected run + cut-out state when switching projects.
  useEffect(() => {
    setRunId('');
    cutoutSettled.current = false;
    setCutout(null);
    setRenders([]);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const tick = () => {
      api.getMagic(projectId, runId || undefined).then((m) => alive && setMagic(m)).catch(() => undefined);
      // Skip once settled - keeps the ready video's url (and playback) stable.
      if (!cutoutSettled.current) {
        api.getCutout(projectId).then((c) => {
          if (!alive) return;
          setCutout(c);
          if (c.status === 'ready' || c.status === 'failed') cutoutSettled.current = true;
        }).catch(() => undefined);
      }
      api.getRecentRenders(projectId).then((r) => alive && setRenders(r.jobs)).catch(() => undefined);
    };
    tick();
    // Poll only while viewing the latest/live run; a pinned past run is static.
    const t = runId ? 0 : window.setInterval(tick, 2000);
    return () => {
      alive = false;
      if (t) window.clearInterval(t);
    };
  }, [projectId, runId]);

  const runs = magic?.runs ?? [];

  const pct = Math.round((magic?.progress ?? 0) * 100);
  const log = useMemo(() => [...(magic?.log ?? [])].reverse(), [magic]);

  return (
    <div>
      <div style={S.top}>
        <select style={S.select} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Select a project…</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {projectId && runs.length > 0 && (
          <select style={S.select} value={runId} onChange={(e) => setRunId(e.target.value)}>
            <option value="">Latest run (live)</option>
            {runs.map((r) => (
              <option key={r.id} value={r.id}>
                {r.startedAt ? new Date(r.startedAt).toLocaleString() : r.id} · {r.status}
                {r.instruction ? ` · "${r.instruction.slice(0, 24)}"` : ''}
              </option>
            ))}
          </select>
        )}
        {runs.length > 0 && <span style={{ color: '#666', fontSize: 12 }}>{runs.length} run{runs.length > 1 ? 's' : ''}</span>}
      </div>

      <CaptionPreviewGallery />

      {!projectId ? (
        <div style={S.empty}>Pick a project to inspect its kMagic run.</div>
      ) : (!magic || magic.status === 'idle') && (!cutout || cutout.status === 'idle') && renders.length === 0 ? (
        <div style={S.empty}>No kMagic run, cut-out or render for this project yet.</div>
      ) : (
        <div style={S.grid}>
          {magic && magic.status !== 'idle' && (
            <section style={S.card}>
              <h3 style={S.h3}>Status</h3>
              <div style={S.statusRow}>
                <Badge status={magic.status} />
                <span style={{ color: '#aaa' }}>{magic.stage}</span>
              </div>
              <div style={S.bar}><div style={{ ...S.barFill, width: `${pct}%` }} /></div>
              <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>{pct}%</div>
              {magic.instruction && (
                <p style={S.instr}><b style={{ color: '#ccc' }}>Instruction:</b> {magic.instruction}</p>
              )}
              {magic.error && <p style={{ color: '#ff8080' }}>{magic.error}</p>}
            </section>
          )}

          {magic?.trace?.length ? (
            <section style={{ ...S.card, gridColumn: '1 / -1' }}>
              <h3 style={S.h3}>Step-by-step trace ({magic.trace.length})</h3>
              <p style={{ color: '#777', fontSize: 12, margin: '0 0 14px' }}>
                Everything kMagic did, in order - what it saw, what the model replied, and how it changed its own decision. Use this to spot where it goes wrong.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {magic.trace.map((s, i) => (
                  <TraceStep key={i} step={s} />
                ))}
              </div>
            </section>
          ) : null}

          {magic && magic.status !== 'idle' && (
            <section style={S.card}>
              <h3 style={S.h3}>Sampled frames ({magic.frameUrls?.length ?? 0})</h3>
              <div style={S.frames}>
                {(magic.frameUrls ?? []).map((u, i) => (
                  <img key={i} src={u} style={S.frame} alt="" />
                ))}
                {!magic.frameUrls?.length && <span style={{ color: '#666' }}>No frames yet.</span>}
              </div>
            </section>
          )}

          <section style={S.card}>
            <h3 style={S.h3}>Person cut-out (3D depth)</h3>
            <div style={S.statusRow}>
              <Badge status={cutout?.status ?? 'idle'} />
              {cutout?.width && <span style={{ color: '#888', fontSize: 12 }}>{cutout.width}×{cutout.height}</span>}
            </div>
            {cutout?.status === 'running' && (
              <>
                <div style={S.bar}><div style={{ ...S.barFill, width: `${Math.round((cutout.progress ?? 0) * 100)}%` }} /></div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 6 }}>{Math.round((cutout.progress ?? 0) * 100)}%</div>
              </>
            )}
            {cutout?.error && <p style={{ color: '#ff8080', fontSize: 13 }}>{cutout.error}</p>}
            {cutout?.status === 'ready' && cutout.url ? (
              <div style={S.checker}>
                <video src={cutout.url} style={S.cutoutVid} controls muted loop playsInline autoPlay />
              </div>
            ) : cutout?.status !== 'running' ? (
              <span style={{ color: '#666', fontSize: 13 }}>No cut-out generated yet.</span>
            ) : null}
            <p style={{ color: '#666', fontSize: 11, marginTop: 8 }}>
              Alpha WebM (VP9). The checkerboard shows through where the background was removed.
            </p>
          </section>

          <section style={{ ...S.card, gridColumn: '1 / -1' }}>
            <h3 style={S.h3}>Render jobs ({renders.length})</h3>
            {renders.length === 0 ? (
              <span style={{ color: '#666' }}>No renders yet.</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {renders.map((j) => {
                  const rpct = Math.round((j.progress ?? 0) * 100);
                  const secs = j.elapsedMs != null ? Math.round(j.elapsedMs / 1000) : null;
                  return (
                    <div key={j.id} style={S.renderRow}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Badge status={j.status} />
                        <span style={{ color: '#cfd', fontSize: 13, fontWeight: 600 }}>{j.stage}</span>
                        <span style={{ color: '#888', fontSize: 12 }}>{rpct}%</span>
                        {(j.resolution || j.fps) && (
                          <span style={{ color: '#888', fontSize: 12 }}>
                            {j.aspectRatio ?? ''} {j.resolution ?? ''}{j.fps ? ` · ${j.fps}fps` : ''}
                          </span>
                        )}
                        {secs != null && <span style={{ color: '#888', fontSize: 12 }}>{secs}s{j.status === 'processing' ? ' (running)' : ''}</span>}
                        {j.attempts > 1 && <span style={{ color: '#f5a623', fontSize: 12 }}>attempt {j.attempts}</span>}
                        <span style={{ marginLeft: 'auto', color: '#555', fontSize: 11, fontFamily: 'monospace' }}>{j.id.slice(-8)}</span>
                      </div>
                      <div style={{ ...S.bar, marginTop: 8 }}><div style={{ ...S.barFill, width: `${rpct}%` }} /></div>
                      {j.error && <p style={{ color: '#ff8080', fontSize: 12, margin: '8px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{j.error}</p>}
                      {j.output && (
                        <p style={{ color: '#666', fontSize: 11, margin: '6px 0 0' }}>
                          output {j.output.width}×{j.output.height} · {(j.output.durationMs / 1000).toFixed(1)}s
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {magic && magic.status !== 'idle' && (
            <section style={S.card}>
              <h3 style={S.h3}>AI plan (result)</h3>
              <pre style={S.pre}>{magic.result ? JSON.stringify(magic.result, null, 2) : '-'}</pre>
            </section>
          )}

          {magic && magic.status !== 'idle' && (
            <section style={S.card}>
              <h3 style={S.h3}>Agentic log</h3>
              <div style={S.logList}>
                {log.map((e, i) => (
                  <div key={i} style={S.logRow}>
                    <span style={{ ...S.logKind, ...kindColor(e.kind) }}>{e.kind}</span>
                    <span style={{ color: '#ddd' }}>{e.message}</span>
                    <span style={S.logAt}>{new Date(e.at).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Badge({ status }: { status: string }) {
  const c: Record<string, string> = { running: '#f5a623', processing: '#f5a623', queued: '#8aa0ff', done: '#39d98a', completed: '#39d98a', failed: '#ff6b6b', cancelled: '#999', applied: '#6b8cff', idle: '#777' };
  return <span style={{ ...S.badge, background: (c[status] ?? '#777') + '22', color: c[status] ?? '#777' }}>{status}</span>;
}
function kindColor(k: string): { color: string } {
  const c: Record<string, string> = { error: '#ff6b6b', ai: '#a06bff', 'ai-request': '#42c6ff', frames: '#39d98a', stage: '#f5a623', info: '#888' };
  return { color: c[k] ?? '#888' };
}

const TRACE_KIND_COLOR: Record<string, string> = {
  sample: '#39d98a',
  'ai-request': '#42c6ff',
  'ai-response': '#a06bff',
  plan: '#f5a623',
  stock: '#39d98a',
  critique: '#42c6ff',
  'review-request': '#42c6ff',
  'review-response': '#a06bff',
  revise: '#ff6fae',
  info: '#888',
};

/** One step in the kMagic trace: title, kind, text, the images the AI saw, and
 *  the structured data (collapsed by default). */
function TraceStep({ step }: { step: MagicTraceStep }) {
  const [showData, setShowData] = useState(false);
  const color = TRACE_KIND_COLOR[step.kind] ?? '#888';
  return (
    <div style={{ ...S.renderRow, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...S.badge, background: color + '22', color, fontSize: 10.5, textTransform: 'uppercase' }}>{step.kind}</span>
        <span style={{ color: '#e7e7ea', fontSize: 14, fontWeight: 700 }}>{step.title}</span>
        <span style={{ marginLeft: 'auto', color: '#555', fontSize: 11 }}>{new Date(step.at).toLocaleTimeString()}</span>
      </div>
      {step.text && (
        <p style={{ color: '#b9bdc9', fontSize: 12.5, lineHeight: 1.5, margin: '10px 0 0', whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>{step.text}</p>
      )}
      {!!step.imageUrls?.length && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {step.imageUrls.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer">
              <img src={u} style={{ height: 132, width: 'auto', borderRadius: 6, border: '1px solid #2a2d38', display: 'block' }} alt="" />
            </a>
          ))}
        </div>
      )}
      {step.data !== undefined && step.data !== null && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowData((v) => !v)} style={S.dataToggle}>
            {showData ? '▾ hide data' : '▸ show data'}
          </button>
          {showData && <pre style={{ ...S.pre, marginTop: 8, maxHeight: 420 }}>{JSON.stringify(step.data, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#0d0e12', color: '#e7e7ea', fontFamily: 'Inter, system-ui, sans-serif', padding: 20 },
  nav: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #23262f' },
  tab: { background: 'transparent', color: '#8b8f9c', border: '1px solid transparent', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  tabActive: { background: '#191b22', color: '#e7e7ea', border: '1px solid #2a2d38' },
  pill: { borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700 },
  userRow: { background: '#0f1117', border: '1px solid #23262f', borderRadius: 10, overflow: 'hidden' },
  userHead: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: '#e7e7ea', padding: '11px 14px', cursor: 'pointer', fontSize: 14 },
  userBody: { padding: '4px 14px 14px 38px', borderTop: '1px solid #191b22' },
  projRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#14161d', border: '1px solid #23262f', borderRadius: 8, padding: '8px 12px' },
  usageChip: { borderRadius: 6, padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap' },
  usageSummary: { background: '#0d0f15', border: '1px solid #23262f', borderRadius: 8, padding: '10px 12px', marginBottom: 12 },
  top: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 },
  brand: { fontWeight: 800, fontSize: 16, background: 'linear-gradient(100deg,#ff6fae,#a06bff,#42c6ff)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' },
  select: { background: '#191b22', color: '#e7e7ea', border: '1px solid #2a2d38', borderRadius: 8, padding: '7px 10px' },
  link: { marginLeft: 'auto', color: '#8aa0ff', textDecoration: 'none' },
  empty: { color: '#777', padding: 40, textAlign: 'center' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' },
  card: { background: '#14161d', border: '1px solid #23262f', borderRadius: 14, padding: 16 },
  h3: { margin: '0 0 12px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8b8f9c' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  badge: { borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  bar: { height: 8, background: '#23262f', borderRadius: 999, overflow: 'hidden' },
  barFill: { height: '100%', background: 'linear-gradient(90deg,#ff6fae,#a06bff,#42c6ff)', transition: 'width 0.4s' },
  instr: { color: '#aaa', fontSize: 13, marginTop: 12 },
  frames: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  frame: { width: 92, height: 'auto', borderRadius: 6, border: '1px solid #2a2d38' },
  checker: {
    marginTop: 4,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid #2a2d38',
    // A checkerboard so the removed background (alpha) is visibly transparent.
    backgroundColor: '#3a3d47',
    backgroundImage:
      'linear-gradient(45deg,#4a4e5a 25%,transparent 25%),linear-gradient(-45deg,#4a4e5a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#4a4e5a 75%),linear-gradient(-45deg,transparent 75%,#4a4e5a 75%)',
    backgroundSize: '20px 20px',
    backgroundPosition: '0 0,0 10px,10px -10px,-10px 0',
    display: 'inline-block',
  },
  cutoutVid: { display: 'block', maxWidth: 260, height: 'auto', borderRadius: 8 },
  renderRow: { background: '#0f1117', border: '1px solid #23262f', borderRadius: 10, padding: 12 },
  pre: { margin: 0, fontSize: 12, lineHeight: 1.5, color: '#c9d1ea', whiteSpace: 'pre-wrap', maxHeight: 340, overflow: 'auto' },
  logList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflow: 'auto' },
  logRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 },
  logKind: { minWidth: 76, fontWeight: 700, fontSize: 10.5, textTransform: 'uppercase' },
  logAt: { marginLeft: 'auto', color: '#666', fontSize: 11 },
  dataToggle: { background: '#0f1117', color: '#8aa0ff', border: '1px solid #23262f', borderRadius: 6, padding: '4px 10px', fontSize: 11.5, cursor: 'pointer' },
};
